"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { BlogsData } from "@/types/data";
import { getSectionData } from "@/lib/actions/data.actions";
import { fetchBlogs, BlogsResponse, BlogPost } from "@/lib/actions/blog.actions";
import { ArrowLeft, ArrowRight, MoveRight } from "lucide-react";
import Image from "next/image";
import { formatDate } from "@/lib/helper";
import BlogCardSkeleton from "./BlogCardSkeleton";
import { gsap } from "@/lib/gsap";

export default function BlogContent({ showAll }: { showAll: boolean }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [blogsData, setBlogsData] = useState<BlogsResponse>();
  const [isMobile, setIsMobile] = useState(false);
  const [blogsHeaderData, setBlogsHeaderData] = useState<BlogsData>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadBlogs = async (page: number) => {
      try {
        setIsLoading(true);
        const [blogsHeaderData, blogsResponse] = await Promise.all([
          getSectionData("blogs"),
          fetchBlogs({ page, perPage: 9 }),
        ]);
        setBlogsData(blogsResponse);
        setBlogsHeaderData(blogsHeaderData);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadBlogs(currentPage);
  }, [currentPage]);

  const { tagline, title, description } = blogsHeaderData?.content || {};
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Use new CMS API response format (posts instead of results)
  const posts: BlogPost[] = blogsData?.posts || [];

  const displayedBlogs = showAll ? posts : posts.slice(0, 3);

  // Use new CMS API pagination format
  const totalCount = blogsData?.pagination?.total_posts || 0;
  const totalPages = blogsData?.pagination?.total_pages || 1;

  const handlePageChange = (pg: number) => {
    setBlogsData(undefined);
    setCurrentPage(pg);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 0);
  };

  const isPrevDisabled = currentPage <= 1 || !blogsData?.pagination?.has_previous;

  const isNextDisabled =
    !blogsData?.pagination?.has_next || currentPage >= totalPages || posts.length === 0;

  const sectionRef = useRef<HTMLElement>(null);

  const headerRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoading || !blogsData) return;
    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 90%",
          end: "top 45%",
          toggleActions: "play none none reverse",
        },
      });

      timeline

        .fromTo(
          headerRef.current,
          { opacity: 0, scale: 0.95 },
          { opacity: 1, scale: 1, duration: 1.2, ease: "power2.out" }
        )
        .fromTo(
          headingRef.current,
          { opacity: 0, y: 40, filter: "blur(8px)" },
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 1,
            ease: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          },
          " -=0.1"
        );

      const blogCards = carouselRef.current?.children;
      if (blogCards && blogCards.length > 0) {
        gsap.set(blogCards, {
          opacity: 0,
          y: 40,
          scale: 0.95,
          filter: "blur(8px)",
        });

        gsap.to(blogCards, {
          opacity: 1,
          y: 0,
          scale: 1,
          filter: "blur(0px)",
          duration: 0.8,
          ease: "power2.out",
          stagger: 0.2,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 75%",
            end: "top 40%",
            toggleActions: "play none none reverse",
          },
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, [isLoading, blogsData]);

  return (
    <>
      {}
      <section ref={sectionRef} className="container pb-16 md:pb-24">
        <div className=" text-center max-w-2xl mx-auto mb-12">
          {!showAll && blogsHeaderData && (
            <>
              {tagline && (
                <div
                  className="flex items-center justify-center  mb-4"
                  ref={headerRef}
                >
                  <div className="w-5 h-0.5 bg-theme-primary mr-3"></div>
                  <p className="text-xs font-semibold text-theme-primary tracking-wider uppercase">
                    {tagline}
                  </p>
                </div>
              )}
              {title && (
                <h2
                  className="text-4xl md:text-5xl font-bold text-theme-secondary"
                  ref={headingRef}
                >
                  {title}
                </h2>
              )}
              {description && (
                <p className="text-lg text-theme-secondary-lightest">
                  {description}
                </p>
              )}
            </>
          )}
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mx-auto"
          ref={carouselRef}
        >
          {isLoading ? (
            <BlogCardSkeleton count={showAll ? 6 : 3} />
          ) : displayedBlogs.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-500 text-lg">
                No blog posts available yet. Check back soon!
              </p>
            </div>
          ) : (
            displayedBlogs.map((post, index) => {
              // Support both old API (image) and new CMS API (featured_image)
              const postImage = post.featured_image || (post as unknown as { image?: string }).image;
              // Support both old API (description) and new CMS API (excerpt)
              const postExcerpt = post.excerpt || (post as unknown as { description?: string }).description;
              // Support both old API (created_at) and new CMS API (published_at)
              const postDate = post.published_at || (post as unknown as { created_at?: string }).created_at;
              // Use category name from new CMS API, fallback to tags for old API
              const categoryName = post.category?.name || (post.tags && post.tags.length > 0 ? post.tags[0] : "");

              return (
                <Link
                  href={`/blogs/${post.slug}`}
                  key={post.id || index}
                  className="border border-[#F3F4F6] overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 bg-white h-full flex flex-col rounded-lg max-w-[500px] mx-auto md:max-w-none opacity-0"
                >
                  <div className="w-full h-48 overflow-hidden ">
                    {postImage && (
                      <Image
                        src={postImage}
                        alt={post.featured_image_alt || post.title || "Blog Image"}
                        width={600}
                        height={400}
                        quality={75}
                        sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 400px"
                        className="w-full h-full object-cover"
                        loading="lazy"
                        fetchPriority="low"
                      />
                    )}
                  </div>
                  <div className="p-4 md:p-6 flex flex-col flex-1 justify-between ">
                    <div className="flex items-center justify-between text-sm text-[#b0c0d3] mb-2 font-semibold">
                      <span className="font-semibold text-theme-primary text-sm">
                        {categoryName}
                      </span>
                      {postDate && (
                        <span className="text-[12.8px] text-[#6B7280]">
                          {formatDate(postDate)}
                        </span>
                      )}
                    </div>
                    {post.title && (
                      <h3 className="text-xl font-medium text-[#284D93] mb-3">
                        {post.title}
                      </h3>
                    )}
                    {postExcerpt && (
                      <p className="text-[#4B5563] mb-4 text-base line-clamp-4 leading-[20px]">
                        {postExcerpt}
                      </p>
                    )}

                    <div className="flex items-center gap-1 font-semibold text-theme-secondary-darker">
                      Read More <MoveRight className="w-3 h-3 mt-1" />
                      <span className="sr-only"> {post.title} </span>
                    </div>
                  </div>
                </Link>
              );
            })
          )}
        </div>

        {showAll && totalPages > 1 && (
          <div className="flex justify-center items-center mt-12">
            <nav className="flex items-center gap-2" aria-label="Pagination">
              {}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={isPrevDisabled}
                aria-label="Previous page"
                className={`size-8 md:size-10  inline-flex justify-center items-center rounded-full border transition-all duration-300 font-medium
                   ${
                     isPrevDisabled
                       ? "cursor-not-allowed bg-gray-100 border-gray-200 text-gray-400"
                       : "cursor-pointer bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                   }`}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>

              {}
              {(() => {
                const maxVisiblePages = isMobile ? 3 : 7;
                let startPage = 1;
                let endPage = totalPages;

                if (totalPages > maxVisiblePages) {
                  const middlePage = Math.ceil(maxVisiblePages / 2);
                  if (currentPage <= middlePage) {
                    startPage = 1;
                    endPage = maxVisiblePages;
                  } else if (currentPage >= totalPages - middlePage + 1) {
                    startPage = totalPages - maxVisiblePages + 1;
                    endPage = totalPages;
                  } else {
                    startPage = currentPage - middlePage + 1;
                    endPage = currentPage + middlePage - 1;
                  }
                }

                const pages = [];

                if (startPage > 1) {
                  pages.push(
                    <button
                      key={1}
                      onClick={() => handlePageChange(1)}
                      className="size-8 md:size-10 cursor-pointer flex items-center justify-center text-sm md:text-base font-semibold rounded-full border transition-all duration-300 bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                    >
                      1
                    </button>
                  );
                  if (startPage > 2) {
                    pages.push(
                      <span
                        key="ellipsis-start"
                        className="size-8 md:size-10 cursor-pointer flex items-center justify-center text-gray-400 text-base md:text-lg font-bold select-none"
                      >
                        ···
                      </span>
                    );
                  }
                }

                for (let pg = startPage; pg <= endPage; pg++) {
                  const isActive = pg === currentPage;
                  pages.push(
                    <button
                      key={pg}
                      onClick={() => handlePageChange(pg)}
                      aria-label={`Page ${pg}`}
                      aria-current={isActive ? "page" : undefined}
                      disabled={isActive}
                      className={` size-8 md:size-10 flex items-center justify-center text-sm md:text-base font-semibold rounded-full border transition-all duration-300 
                         ${
                           isActive
                             ? "bg-gray-800 text-white border-gray-800 cursor-default"
                             : "cursor-pointer bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                         }`}
                    >
                      {pg}
                    </button>
                  );
                }

                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(
                      <span
                        key="ellipsis-end"
                        className="h-10 w-10 md:size-8 flex items-center justify-center text-gray-400 text-base md:text-lg font-bold select-none"
                      >
                        ···
                      </span>
                    );
                  }
                  pages.push(
                    <button
                      key={totalPages}
                      onClick={() => handlePageChange(totalPages)}
                      aria-label={`Page ${totalPages}`}
                      className=" h-10 w-10 md:size-8 flex items-center justify-center text-sm md:text-base font-semibold rounded-full border transition-all duration-300 bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                    >
                      {totalPages}
                    </button>
                  );
                }

                return pages;
              })()}

              {}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={isNextDisabled}
                aria-label="Next page"
                className={`size-8 md:size-10 inline-flex justify-center items-center rounded-full border transition-all duration-300 font-medium
                   ${
                     isNextDisabled
                       ? "cursor-not-allowed bg-gray-100 border-gray-200 text-gray-400"
                       : "cursor-pointer bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                   }`}
              >
                <ArrowRight className="w-5 h-5" />
              </button>
            </nav>
          </div>
        )}
      </section>
    </>
  );
}
